import { useState } from "react";
import { ShieldCheck, TrendingUp, Coins, Sparkles } from "lucide-react";
import { onlineFriends, suggestedCommunities, trendingTopics } from "@/lib/mockData";
import { usePro } from "@/context/ProContext";
import UpgradeModal from "@/components/pro/UpgradeModal";

export default function RightRail() {
  const pro = usePro();
  const [open, setOpen] = useState(false);
  return (
    <aside
      data-testid="right-rail"
      className="hidden xl:flex w-[320px] shrink-0 flex-col gap-5 sticky top-20 h-[calc(100vh-96px)] overflow-y-auto pb-8 pr-1"
    >
      {/* Pro / Earnings card */}
      <div className={`border hairline rounded-2xl p-5 ${pro?.is_pro ? "bg-ink text-cream" : "bg-cream-card"}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {pro?.is_pro ? <Sparkles className="w-4 h-4 text-coral" /> : <Coins className="w-4 h-4 text-coral" />}
            <p className={`font-mono text-[10px] uppercase tracking-[0.25em] ${pro?.is_pro ? "text-cream/65" : "text-ink-mid"}`}>
              {pro?.is_pro ? "Ovamx PRO · Activo" : "Tus ingresos · mes"}
            </p>
          </div>
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${pro?.is_pro ? "bg-coral text-cream" : "bg-coral/15 text-coral"}`}>
            {pro?.is_pro ? "FIRMA" : "+24%"}
          </span>
        </div>
        {pro?.is_pro ? (
          <>
            <p className="font-serif text-3xl tracking-tighter">Filtro Original activo</p>
            <p className="mt-2 text-xs text-cream/75 leading-relaxed">
              Tus posts llevan el sello «Ovamx Original» y entran a la curaduría priorizada de Horizonte.
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <Mini dark k="$1,284" v="ingresos" />
              <Mini dark k="3×" v="alcance" />
              <Mini dark k="0%" v="comisión" />
            </div>
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-4xl tracking-tighter">$1,284</span>
              <span className="text-xs text-ink-mid">USD</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <Mini k="$842" v="contenido" />
              <Mini k="$310" v="propinas" />
              <Mini k="$132" v="ventas" />
            </div>
            <button onClick={() => setOpen(true)} data-testid="rail-monetize" className="mt-4 inline-flex items-center justify-center w-full gap-2 bg-ink text-cream rounded-full py-2.5 text-xs hover:bg-coral transition-colors">
              <Sparkles className="w-3.5 h-3.5" />
              Activar Ovamx PRO · $9/mes
            </button>
          </>
        )}
      </div>

      {/* Trending */}
      <div className="border hairline bg-white rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-coral" />
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">Tendencias hoy</p>
        </div>
        <ul className="space-y-3">
          {trendingTopics.map((t) => (
            <li key={t.id} data-testid={`trend-${t.id}`} className="flex items-start justify-between gap-3 group cursor-pointer">
              <div className="min-w-0">
                <p className="text-sm text-ink group-hover:text-coral transition truncate">{t.title}</p>
                <p className="text-[11px] text-ink-mid font-mono">{t.count}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Suggested communities */}
      <div className="border hairline bg-white rounded-2xl p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-3">Comunidades sugeridas</p>
        <ul className="space-y-3">
          {suggestedCommunities.map((c) => (
            <li key={c.id} data-testid={`community-${c.id}`} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cream-deep flex items-center justify-center font-serif text-lg">
                {c.name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-ink truncate">{c.name}</p>
                <p className="text-[11px] text-ink-mid">{c.members} · {c.tag}</p>
              </div>
              <button className="text-xs px-3 py-1.5 rounded-full border hairline hover:bg-coral hover:text-cream hover:border-coral transition">
                Unirme
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Online friends */}
      <div className="border hairline bg-white rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">Vínculos en línea</p>
          <span className="text-[10px] text-coral font-mono">{onlineFriends.filter(f=>f.online).length} ahora</span>
        </div>
        <ul className="space-y-2.5">
          {onlineFriends.map((f) => (
            <li key={f.id} data-testid={`friend-${f.id}`} className="flex items-center gap-3 hover:bg-cream-deep/30 rounded-lg p-1.5 cursor-pointer transition">
              <span className="relative">
                <img src={f.avatar} alt="" className="w-8 h-8 rounded-full" />
                <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-white ${f.online ? "bg-coral" : "bg-ink-mid/40"}`} />
              </span>
              <span className="text-sm text-ink/85 truncate">{f.name}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Footer micro */}
      <div className="px-2 py-3 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-ink-mid">
        <ShieldCheck className="w-3.5 h-3.5 text-coral" />
        Privacidad por diseño
      </div>

      <UpgradeModal open={open} onClose={() => setOpen(false)} />
    </aside>
  );
}

function Mini({ k, v, dark }) {
  return (
    <div className={`rounded-lg py-2 ${dark ? "bg-cream/[0.06]" : "bg-cream-deep/50"}`}>
      <div className={`text-sm font-medium ${dark ? "text-cream" : "text-ink"}`}>{k}</div>
      <div className={`text-[10px] font-mono uppercase tracking-wider ${dark ? "text-cream/55" : "text-ink-mid"}`}>{v}</div>
    </div>
  );
}
